import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const login = async (email, password) => {
        try {
            const response = await api.post('/auth/login', { email, password });
            const { token, user } = response.data;

            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify(user));
            setUser(user);

            return { success: true, user };
        } catch (error) {
            return {
                success: false,
                error: error.response?.data?.error || 'Login failed',
            };
        }
    };

    const register = async (companyData) => {
        try {
            const response = await api.post('/auth/register', companyData);
            const { token, user } = response.data;

            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify(user));
            setUser(user);

            return { success: true, user };
        } catch (error) {
            return {
                success: false,
                error: error.response?.data?.error || 'Registration failed',
            };
        }
    };

    const logout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
    };

    const updateUser = (updatedUser) => {
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setUser(updatedUser);
    };

    useEffect(() => {
        // Check if user is logged in on mount and refresh data
        const checkAuth = async () => {
            const token = localStorage.getItem('token');
            const savedUser = localStorage.getItem('user');

            if (token) {
                // Optimistically set user from storage
                if (savedUser) {
                    try {
                        setUser(JSON.parse(savedUser));
                    } catch (e) {
                        console.error('Error parsing saved user', e);
                    }
                }

                // Fetch fresh user data from API
                try {
                    const response = await api.get('/auth/me');
                    if (response.data && response.data.user) {
                        const freshUser = response.data.user;
                        setUser(freshUser);
                        localStorage.setItem('user', JSON.stringify(freshUser));
                    }
                } catch (error) {
                    console.error('Failed to refresh user data:', error);
                    // If 401 Unauthorized, verify if we should logout
                    if (error.response && error.response.status === 401) {
                        logout();
                    }
                }
            }
            setLoading(false);
        };

        checkAuth();
    }, []);

    // Computed helpers for unified user model
    const isAdmin = !!(user?.is_admin);
    const permissions = user?.permissions || {};

    /**
     * Check if the current user has a specific permission.
     * For simple permissions: returns true/false
     * For scoped permissions: checks if user's scope >= required scope
     * @param {string} permissionKey - e.g., 'activity.create', 'employee.view_details'
     * @param {string} [requiredScope] - optional: 'own', 'team', or 'all'
     * @returns {boolean}
     */
    const checkPermission = useCallback((permissionKey, requiredScope = null) => {
        // Admins (company creators) have all permissions
        if (isAdmin) return true;

        const value = permissions[permissionKey];

        // Not set or explicitly denied
        if (value === undefined || value === null || value === false || value === 'none') {
            return false;
        }

        // Simple boolean permission
        if (value === true) return true;

        // Scoped permission
        if (requiredScope) {
            const scopeHierarchy = ['none', 'own', 'team', 'all'];
            const userScopeIndex = scopeHierarchy.indexOf(value);
            const requiredScopeIndex = scopeHierarchy.indexOf(requiredScope);
            return userScopeIndex >= requiredScopeIndex;
        }

        // Any non-none scope grants access
        return true;
    }, [isAdmin, permissions]);

    const contextValue = useMemo(() => ({
        user,
        login,
        register,
        logout,
        updateUser,
        loading,
        isAdmin,
        permissions,
        checkPermission,
    }), [user, loading, isAdmin, permissions, checkPermission]);

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};


